import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import "../styles/FeaturedEvents.scss";
import Title from "./common/Title";
import SliderNavigation from "./common/SliderNavigation";
import { dateOnlyFormat } from "../utils/dateFormats";

const FeaturedEvents = ({ events = [] }) => {
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [eventsPerView, setEventsPerView] = useState(3);

  /* =============================
     FILTER + SORT EVENTS
     ============================= */
  const filteredEvents = useMemo(() => {
    const now = new Date();
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);

    return events
      .filter(event => {
        const endDate = new Date(event.end_date);
        return endDate >= todayStart; // include today + future
      })
      .sort(
        (a, b) => new Date(a.start_date) - new Date(b.start_date)
      );
  }, [events]);

  /* =============================
     RESPONSIVE EVENTS PER VIEW
     ============================= */
  useEffect(() => {
    const updatePerView = () => {
      const width = window.innerWidth;
      if (width < 576) setEventsPerView(1);
      else if (width < 992) setEventsPerView(2);
      else setEventsPerView(3);
    };

    updatePerView();
    window.addEventListener("resize", updatePerView);
    return () => window.removeEventListener("resize", updatePerView);
  }, []);

  /* =============================
     DESKTOP SLIDER FUNCTIONS
     ============================= */
  const nextSlide = () => {
    if (filteredEvents.length > eventsPerView) {
      setCurrentSlide(
        prev => (prev + eventsPerView) % filteredEvents.length
      );
    }
  };

  const prevSlide = () => {
    if (filteredEvents.length > eventsPerView) {
      setCurrentSlide(
        prev =>
          (prev - eventsPerView + filteredEvents.length) %
          filteredEvents.length
      );
    }
  };

  const getVisibleEvents = () => {
    if (!filteredEvents.length) return [];

    const visible = [];
    const count = Math.min(eventsPerView, filteredEvents.length);

    for (let i = 0; i < count; i++) {
      visible.push(
        filteredEvents[(currentSlide + i) % filteredEvents.length]
      );
    }

    return visible;
  };

  /* =============================
     MOBILE AUTO SLIDE
     ============================= */
  useEffect(() => {
    if (window.innerWidth < 768 && filteredEvents.length > 1) {
      const interval = setInterval(() => {
        const carousel = document.querySelector("#mobileEventCarousel");
        if (carousel && window.bootstrap) {
          const bsCarousel =
            window.bootstrap.Carousel.getOrCreateInstance(carousel);
          bsCarousel.next();
        }
      }, 3000);

      return () => clearInterval(interval);
    }
  }, [filteredEvents]);

  return (
    <section id="featured-events" className="featured-events">
      <Title text="FEATURED EVENTS" />

      {/* ================= DESKTOP ================= */}
      <div className="d-none d-md-block carousel-wrapper">
        {filteredEvents.length > eventsPerView && (
          <SliderNavigation
            onPrevClick={prevSlide}
            onNextClick={nextSlide}
            canPrev={filteredEvents.length > eventsPerView}
            canNext={filteredEvents.length > eventsPerView}
          />
        )}

        <div className="carousel-container">
          <div className="slider-container">
            <div className="events-track">
              {getVisibleEvents().map((event, index) => (
                <div
                  key={index}
                  className="event-card"
                  onClick={() =>
                    navigate(`/event/${event.id}`, {
                      state: { event },
                    })
                  }
                >
                  <div className="image-container">
                    <img src={event.thumbnail} alt={event.heading} />
                  </div>

                  <div className="content">
                    <h3 className="title">{event.heading}</h3>

                    {event.subtitle && (
                      <p className="subtitle">{event.subtitle}</p>
                    )}

                    <p className="location">{event.venue}</p>

                    <div className="datetime">
                      <span className="date">
                        {dateOnlyFormat(event.start_date)}
                        {event.end_date &&
                          ` - ${dateOnlyFormat(event.end_date)}`}
                      </span>
                      <span className="time">
                        {event.time}
                      </span>
                    </div>

                    <div className="cta-btn">View Details</div>
                  </div>
                </div>
              ))}

              {filteredEvents.length === 0 && (
                <p className="no-events">
                  No featured events available
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ================= MOBILE ================= */}
      <div className="d-md-none">
        <div
          id="mobileEventCarousel"
          className="carousel slide"
          data-bs-ride="carousel"
          data-bs-interval="3000"
        >
          <div className="carousel-inner">
            {filteredEvents.map((event, index) => (
              <div
                key={event.id || index}
                className={`carousel-item ${index === 0 ? "active" : ""
                  }`}
              >
                <div
                  className="event-card"
                  onClick={() =>
                    navigate(`/event/${event.id}`)
                  }
                >
                  <div className="image-container">
                    <img
                      src={event.thumbnail}
                      alt={event.heading}
                    />
                  </div>

                  <div className="content">
                    <h3 className="title">{event.heading}</h3>

                    {event.subtitle && (
                      <p className="subtitle">{event.subtitle}</p>
                    )}

                    <p className="location">{event.venue}</p>

                    <div className="datetime">
                      <span className="date">
                        {dateOnlyFormat(event.start_date)}
                        {event.end_date &&
                          ` - ${dateOnlyFormat(event.end_date)}`}
                      </span>
                      <span className="time">
                        {event.time}
                      </span>
                    </div>

                    <div className="cta-btn">View Details</div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {filteredEvents.length > 1 && (
            <>
              <button
                className="carousel-control-prev"
                type="button"
                data-bs-target="#mobileEventCarousel"
                data-bs-slide="prev"
              >
                <span
                  className="carousel-control-prev-icon"
                  aria-hidden="true"
                ></span>
              </button>

              <button
                className="carousel-control-next"
                type="button"
                data-bs-target="#mobileEventCarousel"
                data-bs-slide="next"
              >
                <span
                  className="carousel-control-next-icon"
                  aria-hidden="true"
                ></span>
              </button>
            </>
          )}
        </div>
      </div>
    </section>
  );
};

export default FeaturedEvents;

